import type { ReactNode } from 'react';
import { Box } from '@mui/material';

import { IntakePageFooter } from './IntakePageFooter';
import { IntakePageHeader } from './IntakePageHeader';
import type { RailProgressState } from './IntakeShell';
import { cardSx, chipSx, groupTitleSx, wf } from './tokens';

export interface ReviewPageSection {
  pageKey: string;
  label: string;
  state: RailProgressState;
  content: ReactNode;
}

interface IntakeReviewPageProps {
  sectionLabel: string;
  items: ReviewPageSection[];
  onContinue: () => void;
  onBack: () => void;
  disabledReason?: string;
  errorMessage?: string;
  primaryLabel?: string;
}

const STATUS_LABELS: Record<RailProgressState, string> = {
  completed: 'Complete',
  inProgress: 'Needs attention',
  notStarted: 'Missing information',
};

export function IntakeReviewPage({
  sectionLabel,
  items,
  onContinue,
  onBack,
  disabledReason,
  errorMessage,
  primaryLabel = 'Looks good',
}: IntakeReviewPageProps) {
  const incompleteCount = items.filter(item => item.state !== 'completed').length;
  const isComplete = items.length > 0 && incompleteCount === 0;
  const completionMessage = isComplete
    ? `${sectionLabel} is complete`
    : `${incompleteCount} section${incompleteCount === 1 ? ' needs' : 's need'} attention`;
  const continueDisabledReason =
    disabledReason ??
    (isComplete ? undefined : 'Complete the missing or invalid information before continuing.');

  return (
    <>
      <IntakePageHeader
        title="Review"
        sub={`Check your ${sectionLabel.toLowerCase()} information and make any corrections here. Changes save the same way as the original pages.`}
      />

      <Box
        role="status"
        sx={{
          ...cardSx,
          background: isComplete ? wf.greenSoft : wf.amberSoft,
          borderColor: isComplete ? wf.green : wf.amber,
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <Box
          component="span"
          aria-hidden="true"
          sx={{ color: isComplete ? wf.green : wf.amber, fontSize: '18px', fontWeight: 700 }}
        >
          {isComplete ? '✓' : '!'}
        </Box>
        <Box>
          <Box sx={{ color: wf.ink, fontSize: '14px', fontWeight: 700 }}>{completionMessage}</Box>
          <Box sx={{ color: wf.muted, fontSize: '12px', mt: '2px' }}>
            {isComplete
              ? 'All required information has been saved.'
              : 'Review the highlighted fields and correct them in place.'}
          </Box>
        </Box>
      </Box>

      {errorMessage && (
        <Box role="alert" sx={{ ...cardSx, background: wf.redSoft, borderColor: wf.red }}>
          {errorMessage}
        </Box>
      )}

      {items.map(item => {
        const complete = item.state === 'completed';
        return (
          <Box
            key={item.pageKey}
            component="section"
            aria-label={`${item.label} review`}
            sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
          >
            <Box
              sx={{
                ...cardSx,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1,
              }}
            >
              <Box sx={groupTitleSx}>{item.label}</Box>
              <Box sx={chipSx(complete ? 'ok' : item.state === 'inProgress' ? 'pend' : 'rev')}>
                {STATUS_LABELS[item.state]}
              </Box>
            </Box>
            {item.content}
          </Box>
        );
      })}

      <IntakePageFooter
        primaryLabel={primaryLabel}
        onPrimary={onContinue}
        onBack={onBack}
        disabled={!isComplete || Boolean(disabledReason)}
        disabledReason={continueDisabledReason}
      />
    </>
  );
}
